/* eslint-disable react/prop-types */
import React, { useCallback, useState, useMemo } from 'react';
import _ from 'underscore';
import { AddImageCrudAdComponent } from '@bingads-webui-campaign-react/add-media-panel-crud-ad';
import { useAssetLibraryImagePicker, useImagePicker, useWebsiteSuggestion } from '@bingads-webui-campaign-react/add-image-hooks';
import { MediaEditorPanel } from '@bingads-webui-campaign-react/media-editor-panel';
import { PivotItemKey } from '@bingads-webui-campaign-react/media-editor';
import { constants } from '@bingads-webui-campaign-react/image-experience-model';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { MediaRepository } from '@bingads-webui-campaign/media-repository';
import { ImageExperienceImage } from '@bingads-webui-campaign-react/image-experience';

import { getStyle } from './style';
import { getImageData } from './image-repository';

const BoostImage = ({
  cid,
  aid,
  i18n,
  permissions,
  oDataConfig,
  instrumentation,
  activityLogger,
  url,
  preferencesService,
  perfMarker,
  onSavePortraitImage,
  associatedImages,
  dateFormat,
  timeZone,
  serverContext,
}) => {
  const maxUploadNum = 1;
  const [isImageEditDisplayed, setIsImageEditDisplayed] = useState(false);
  const [image, setImage] = useState();
  const classes = useDefaultStyles(getStyle);

  const {
    displayImagePicker,
    setDisplayImagePicker,
    selectedMediaPanelSource,
    setSelectedMediaPanelSource,
  } = useImagePicker();

  const mediaRepository = useMemo(() => new MediaRepository({
    cid,
    aid,
    oDataConfig,
  }), [aid, cid, oDataConfig]);

  const onNewImageSelected = useCallback((imagePlacements) => {
    const newImage = imagePlacements[0][0];
    setDisplayImagePicker(false);
    setImage(newImage);
    onSavePortraitImage(newImage);
  }, [setImage, setDisplayImagePicker, onSavePortraitImage]);

  const minCropSize = [470, 940];

  const {
    imagePickerError,
    setImagePickerError,
    clearImagePickerError,
    uploadImage,
    processUploadedImage,
    minSize,
    addImageByUrl,
    getStockImages,
    onSelectStockImage,
    onImageSelected,
    onImagesSelected,
    socialMediaDataSource,
    customMediaSourceConfig,
  } = useAssetLibraryImagePicker({
    cid,
    aid,
    oDataConfig,
    onNewImageSelected,
    i18n,
    permissions,
    isPortraitImage: true,
  });

  const {
    suggestionState,
    suggestionPoller,
  } = useWebsiteSuggestion({
    oDataConfig,
    adContext: {
      advertiserCustomerId: cid,
      accountId: aid,
    },
    instrumentation,
    logActivity: activityLogger.signal.bind(activityLogger),
    permissions,
    fullUrl: url,
    adCreatorScenario: {
      scenarioName: 'BoostImageAdCreation',
      scenarioValue: 'BoostImageAdCreation',
    },
  });

  const { suggestion = {} } = suggestionState;

  const onEdit = () => {
    setIsImageEditDisplayed(true);
  };

  const onRemove = () => {
    setImage({});
    onSavePortraitImage({});
  };

  const onSaveEditImage = (imagePlacement) => {
    image.cropSettings = imagePlacement.cropSettings;
    setImage(image);
    setIsImageEditDisplayed(false);
    onSavePortraitImage(image);
  };

  if (!_.isEmpty(associatedImages) && image === undefined) {
    const portraitImage = associatedImages.find(i => constants.portraitSubtypes[i.Asset.SubType]);
    if (!_.isUndefined(portraitImage)) {
      getImageData(portraitImage.Asset.Id).then((imgObjMT) => {
        window.console.log(imgObjMT);

        const portraitImageExperienceImage = new ImageExperienceImage({
          id: portraitImage.Asset.Id,
          src: imgObjMT.OriginalUrl,
          type: constants.portraitSubtypes[portraitImage.Asset.SubType],
          width: imgObjMT.Width,
          height: imgObjMT.Height,
          associationType: portraitImage.Asset.SubType,
          disableEdit: false,
          isIncluded: true,
          isLogo: false,
          isRecommendedImage: false,
          source: imgObjMT.SourceType,
          cropSettings: {
            SourceX: portraitImage.Asset.CropX,
            SourceY: portraitImage.Asset.CropY,
            SourceHeight: portraitImage.Asset.CropHeight,
            SourceWidth: portraitImage.Asset.CropWidth,
          },
        });
        setImage(portraitImageExperienceImage);
      });
    }
  }

  return (
    <div className="boost-portrait-image">
      <div className="image-section-label">
        <div style={{ fontWeight: 'bold', paddingBottom: 8 }}>{i18n.getString(_TL_('Portrait image'))}</div>
      </div>
      <div className={classes.portraitWithImage}>
        {!(image instanceof ImageExperienceImage) && (
          <button className="image-add btn-hero" onClick={() => setDisplayImagePicker(true)}>{i18n.getString(_TL_('Add image'))}</button>
        )}
        {image instanceof ImageExperienceImage && (
          <React.Fragment>
            <img src={image.src} className={classes.imagePlaceholder} alt="" />
            <button
              aria-label={i18n.getString(_TL_('Edit Image'))}
              className="glyphicon glyphicon-pencil image-edit"
              onClick={onEdit}
            />
            <button
              aria-label={i18n.getString(_TL_('Remove this image'))}
              className="image-delete glyphicon glyphicon-remove"
              onClick={onRemove}
            />
          </React.Fragment>
        )}
      </div>
      <AddImageCrudAdComponent
        isOpen={displayImagePicker}
        setIsOpen={(newOpen) => {
            setDisplayImagePicker(newOpen);
            if (!newOpen) {
              clearImagePickerError();
            }
          }}
        uploadImage={uploadImage}
        processUploadedImage={processUploadedImage}
        minSize={minSize}
        maxUploadNum={maxUploadNum}
        imageExperienceProps={{
          suggestion,
          suggestionPoller,
          finalUrl: url,
          imageOption: constants.imageOptionKeys.Portrait,
          isPortraitImage: true,
        }}
        addImageByUrl={addImageByUrl}
        managedViewSelectionState={selectedMediaPanelSource}
        updateManagedViewSelectionState={setSelectedMediaPanelSource}
        stockImageConfig={{
          getStockImages,
          onSelectStockImage,
        }}
        socialImageConfig={{
          socialMediaDataSource,
          activityLogger,
        }}
        assetLibraryConfig={{
          cid,
          aid,
          oDataConfig,
          dateFormat,
          timeZone,
          preferencesService,
          activityLogger,
          onImageSelected,
          onImagesSelected,
          onImageAddError: e => setImagePickerError(e),
        }}
        i18n={i18n}
        cid={cid.toString()}
        aid={aid.toString()}
        errorMessage={imagePickerError}
        imageType={null}
        customMediaSourceConfig={customMediaSourceConfig}
        perfMarker={perfMarker}
        permissions={permissions}
        serverContext={serverContext}
      />

      {(image instanceof ImageExperienceImage) && (
      <MediaEditorPanel
        isOpen={isImageEditDisplayed}
        onDismiss={() => setIsImageEditDisplayed(false)}
        onApply={onSaveEditImage}
        mediaRepository={mediaRepository}
        aspectRatioConfig={constants.aspectRatiosForBoostPortrait}
        cropSettings={image.cropSettings}
        minCropSize={minCropSize}
        baseImage={{
          Type: image.Type,
          Src: image.Src,
          Width: image.Width,
          Height: image.Height,
        }}
        adCreatorScenario={{
          scenarioName: 'BoostImageAdCreation',
          scenarioValue: 'BoostImageAdCreation',
        }}
        disabledPivotItemKeys={[PivotItemKey.Effect]}
        customizedDefaultSelectedPivotItemKey={PivotItemKey.Crop}
      />)}
      <hr />
    </div>
  );
};

export { BoostImage };
